import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { fetchUserOrders } from '../../services/orderService';

export interface OrderItem {
  id: string
  product_id: string
  quantity: number
  unit_price: number
  total_price: number
}

export interface Order {
  id: string
  order_number: string
  buyer_id: string
  farmer_id: string
  tenant_id: string
  items: OrderItem[]
  total_amount: number
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded'
  delivery_address: {
    street: string
    city: string
    state: string
    postal_code: string
    country: string
  }
  delivery_date?: string
  notes?: string
  created_at: string
  updated_at: string
}

interface OrdersState {
  orders: Order[]
  selectedOrder: Order | null
  isLoading: boolean
  error: string | null
  filters: {
    status: string
    payment_status: string
    date_range: [string, string]
    search: string
  }
  pagination: {
    current_page: number
    total_pages: number
    total_items: number
    items_per_page: number
  }
}

const initialState: OrdersState = {
  orders: [],
  selectedOrder: null,
  isLoading: false,
  error: null,
  filters: {
    status: '',
    payment_status: '',
    date_range: ['', ''],
    search: '',
  },
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10,
  },
}

export const fetchUserOrdersThunk = createAsyncThunk<
  Order[],
  { tenantId: string; userId: string },
  { rejectValue: string }
>('orders/fetchUserOrders', async ({ tenantId, userId }, { rejectWithValue }) => {
  try {
    return await fetchUserOrders(tenantId, userId);
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.detail || err.message || 'Failed to fetch orders');
  }
});

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setOrders: (state, action: PayloadAction<Order[]>) => {
      state.orders = action.payload
    },
    addOrder: (state, action: PayloadAction<Order>) => {
      state.orders.unshift(action.payload)
    },
    updateOrder: (state, action: PayloadAction<Order>) => {
      const index = state.orders.findIndex(order => order.id === action.payload.id)
      if (index !== -1) {
        state.orders[index] = action.payload
      }
    },
    updateOrderStatus: (state, action: PayloadAction<{ id: string; status: Order['status'] }>) => {
      const order = state.orders.find(order => order.id === action.payload.id)
      if (order) {
        order.status = action.payload.status
      }
    },
    setSelectedOrder: (state, action: PayloadAction<Order | null>) => {
      state.selectedOrder = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    updateFilters: (state, action: PayloadAction<Partial<OrdersState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
    setPagination: (state, action: PayloadAction<Partial<OrdersState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserOrdersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserOrdersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload;
        state.error = null;
      })
      .addCase(fetchUserOrdersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch orders';
      });
  },
})

export const {
  setOrders,
  addOrder,
  updateOrder,
  updateOrderStatus,
  setSelectedOrder,
  setLoading,
  setError,
  updateFilters,
  clearFilters,
  setPagination,
} = ordersSlice.actions

export default ordersSlice.reducer
